/**
 * FeesSection — Facility fees table with add/remove
 *
 * Sprint: SPRINT-30
 * Feature: F017 — Section 5 (Fees)
 *
 * Table: Fee name, Amount type, Amount, Frequency, Source chip, Actions
 * Supports manual add and product snapshot rows.
 */

import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeeFormData {
  id?: string;
  sequence: number;
  feeName: string;
  amountType: "fixed" | "percentage";
  amount: string;
  frequency: string;
  source: "manual" | "product_snapshot";
  notes: string | null;
}

export interface FeesSectionProps {
  fees: FeeFormData[];
  onChange: (fees: FeeFormData[]) => void;
}

// ---------------------------------------------------------------------------
// Default factory
// ---------------------------------------------------------------------------

function createDefaultFee(sequence: number): FeeFormData {
  return {
    sequence,
    feeName: "",
    amountType: "fixed",
    amount: "",
    frequency: "one_off",
    source: "manual",
    notes: null,
  };
}

// ---------------------------------------------------------------------------
// Frequency options
// ---------------------------------------------------------------------------

const FEE_FREQUENCIES = [
  { value: "one_off", label: "One-off" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeesSection({ fees, onChange }: FeesSectionProps) {
  const handleAdd = useCallback(() => {
    const newSeq = fees.length > 0 ? fees[fees.length - 1].sequence + 1 : 1;
    onChange([...fees, createDefaultFee(newSeq)]);
  }, [fees, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      const next = fees.filter((_, i) => i !== index);
      const resequenced = next.map((f, i) => ({ ...f, sequence: i + 1 }));
      onChange(resequenced);
    },
    [fees, onChange],
  );

  const handleUpdate = useCallback(
    (index: number, field: keyof FeeFormData, value: string) => {
      const next = [...fees];
      next[index] = {
        ...next[index],
        [field]: value,
        // Flip source to manual if editing a product-snapshot fee
        source: next[index].source === "product_snapshot" ? "manual" : next[index].source,
      };
      onChange(next);
    },
    [fees, onChange],
  );

  return (
    <div data-testid="fees-section" className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Fees</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="fees-add-btn"
          onClick={handleAdd}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Fee
        </Button>
      </div>

      {fees.length === 0 ? (
        <p className="text-sm text-muted-foreground border rounded-lg p-4 bg-muted/20">
          No fees added. Click "Add Fee" to add application, valuation, or other fees.
        </p>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-8 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-0.5">Fee Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[100px]">Type</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[100px]">Amount</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[100px]">Frequency</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[80px]">Source</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[50px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fees.map((fee, index) => (
                <TableRow key={fee.sequence} data-testid={`fee-row-${fee.sequence}`} className="h-10">
                  <TableCell className="px-2 py-0.5">
                    <Input
                      data-testid={`fee-row-${fee.sequence}-name`}
                      value={fee.feeName}
                      onChange={(e) => handleUpdate(index, "feeName", e.target.value)}
                      placeholder="e.g., Application Fee"
                      className="h-8 text-xs"
                    />
                  </TableCell>
                  <TableCell className="px-2 py-0.5">
                    <Select
                      value={fee.amountType}
                      onValueChange={(v) => handleUpdate(index, "amountType", v)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed ($)</SelectItem>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="px-2 py-0.5">
                    <Input
                      data-testid={`fee-row-${fee.sequence}-amount`}
                      type="number"
                      step="0.01"
                      value={fee.amount}
                      onChange={(e) => handleUpdate(index, "amount", e.target.value)}
                      placeholder="0.00"
                      className="h-8 text-xs"
                    />
                  </TableCell>
                  <TableCell className="px-2 py-0.5">
                    <Select
                      value={fee.frequency}
                      onValueChange={(v) => handleUpdate(index, "frequency", v)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FEE_FREQUENCIES.map((f) => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="px-2 py-0.5">
                    <Badge
                      variant={fee.source === "product_snapshot" ? "secondary" : "outline"}
                      data-testid={`fee-row-${fee.sequence}-provenance`}
                      className="text-xs"
                    >
                      {fee.source === "product_snapshot" ? "from product" : "manual"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 py-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      data-testid={`fee-row-${fee.sequence}-remove`}
                      onClick={() => handleRemove(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default FeesSection;
